import React, { FocusEvent, KeyboardEvent, ReactNode, useEffect, useRef } from "react";
import { BuildResult } from "../buildColumnValue";

/** The current value as monday returns it, read fresh when the editor opens. */
export interface ColumnEditState {
  /** Parsed `value` JSON (null when blank). */
  value: any;
  text: string | null;
  /** Parsed column `settings_str`. */
  settings: any;
}

export interface EditorProps {
  state: ColumnEditState;
  boardId: number;
  /** `nextDisplay`, if given, shows in place of the value while it saves. */
  onCommit: (result: BuildResult, nextDisplay?: ReactNode) => void;
  onCancel: () => void;
}

interface EditorFrameProps {
  /** Builds the value to save; called when focus leaves the editor or on Enter. */
  build: () => BuildResult;
  /** `nextDisplay`, if given, shows in place of the value while it saves. */
  onCommit: (result: BuildResult, nextDisplay?: ReactNode) => void;
  onCancel: () => void;
  /** Leaving an untouched editor closes it without writing anything. */
  dirty: boolean;
  /** Long text keeps Enter for new lines and saves on Cmd/Ctrl+Enter instead. */
  multiline?: boolean;
  children: ReactNode;
}

/**
 * monday-style editing: focus leaving the editor or Enter saves, Escape cancels. Wraps the
 * editor's inputs so moving focus between them (e.g. date → time) doesn't save early.
 *
 * The frame itself is focusable. Pressing on something inside it that can't take focus (a
 * checkbox's label, padding) then moves focus to the frame instead of out of the editor, which
 * used to close a dropdown without saving. And an editor with nothing to focus (a board with no
 * tags, a picker still loading) still gets focus, so Escape and clicking away close it.
 */
export const EditorFrame = ({
  build,
  onCommit,
  onCancel,
  dirty,
  multiline,
  children,
}: EditorFrameProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  const commit = () => (dirty ? onCommit(build()) : onCancel());

  useEffect(() => {
    const frame = frameRef.current;
    if (frame && !frame.contains(document.activeElement)) frame.focus();
  }, []);

  // Blur alone misses a click away when focus was already lost: a button that removes itself
  // (a picked entry, a chip's ×) drops focus to the page without any blur event. So a press
  // outside the editor and its value also saves.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const frame = frameRef.current;
      const root = frame?.closest("[data-edit-root]") ?? frame;
      if (!root || cancelledRef.current || root.contains(event.target as Node)) return;
      commitRef.current();
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, []);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (cancelledRef.current) return;
    if (frameRef.current?.contains(event.relatedTarget as Node | null)) return;
    commit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelledRef.current = true;
      onCancel();
    } else if (event.key === "Enter" && (!multiline || event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      commit();
    }
  };

  return (
    <div
      ref={frameRef}
      tabIndex={-1}
      className="flex flex-wrap gap-2 items-center w-full outline-none"
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

export const EDITOR_INPUT_CLASS =
  "min-w-0 flex-1 h-8 px-2 text-sm rounded border border-solid border-[#c3c6d4] focus:border-[#0073ea] focus:outline-none !font-figtree";
